/**
 * Section editor modal — one place for the user to correct the classifier's
 * section labels. Each row has a play-from-here button, the original label,
 * and a dropdown to pick the corrected type. Changes are auto-saved (POST
 * /api/overrides) so the modal feels live.
 *
 * Boundary editing (drag to move start/end) is intentionally out of scope
 * for v1 — ship type corrections first, see how often users need it.
 */

import { useEffect, useRef, useState } from 'react';
import { Play, Pause, X, Check, ChevronDown, Scissors, Plus, Trash2 } from 'lucide-react';
import {
  Section,
  SectionType,
  getSectionLabel,
  getSectionDisplayName,
} from '../utils/sectionClassifier';
import './SectionEditorModal.css';

const PICKER_TYPES: SectionType[] = [
  'mix-in', 'intro', 'build', 'main-drop', 'breakdown',
  'chorus', 'verse', 'bridge', 'outro', 'mix-out',
];

interface SectionEditorModalProps {
  open: boolean;
  onClose: () => void;
  audioUrl: string | null;
  sections: Section[];
  /** Total track duration in seconds — needed to scale the mini timelines. */
  duration: number;
  onSubmitType: (
    sectionIndex: number,
    originalType: SectionType,
    correctedType: SectionType,
  ) => Promise<void>;
  onSubmitBoundary: (
    sectionIndex: number,
    originalType: SectionType,
    originalStartTime: number,
    originalEndTime: number,
    correctedStartTime: number | null,
    correctedEndTime: number | null,
  ) => Promise<void>;
  onSubmitInsert: (
    type: SectionType,
    startTime: number,
    endTime: number,
  ) => Promise<unknown>;
  onEditInsert: (
    insertId: string,
    patch: { type?: SectionType; startTime?: number; endTime?: number },
  ) => Promise<void>;
  onRemoveInsert: (insertId: string) => Promise<void>;
  /** Wipe every recorded correction for this analysis — used by the
   *  "Reset corrections" button in the footer. */
  onClearAll: () => Promise<void>;
}


export function SectionEditorModal({
  open,
  onClose,
  audioUrl,
  sections,
  duration: totalDuration,
  onSubmitType,
  onSubmitBoundary,
  onSubmitInsert,
  onEditInsert,
  onRemoveInsert,
  onClearAll,
}: SectionEditorModalProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [savedIndex, setSavedIndex] = useState<number | null>(null);
  const [openTypeRow, setOpenTypeRow] = useState<number | null>(null);

  // Click-outside closes any open type popover.
  useEffect(() => {
    if (openTypeRow === null) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.row-type-wrap')) setOpenTypeRow(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openTypeRow]);

  // Pause + reset when the modal closes; resume the user's last position when
  // it opens again.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!open) {
      audio.pause();
      setIsPlaying(false);
    }
  }, [open]);

  // ESC closes the modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Audio element listeners.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const seekTo = (t: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = t;
    setCurrentTime(t);
  };

  const playSection = (start: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = start;
    audio.play().catch(() => {});
  };

  const flashSaved = (idx: number) => {
    setSavedIndex(idx);
    window.setTimeout(() => {
      setSavedIndex((s) => (s === idx ? null : s));
    }, 1500);
  };


  if (!open) return null;

  const fmt = (t: number) => {
    if (!isFinite(t) || t < 0) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return renderModal();

  function renderModal() {
    return (
      <div
        className="section-editor-backdrop"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div className="section-editor-modal" onClick={(e) => e.stopPropagation()}>
          {renderHeader()}
          {audioUrl && renderPlayer()}
          {renderList()}
          {renderFooter()}
        </div>
      </div>
    );
  }

  function renderHeader() {
    return (
      <header className="section-editor-header">
        <div>
          <h2>Correct Track Structure</h2>
          <p>Drag the handles to move boundaries — neighbours stay put, so gaps open up. Click <b>Add section</b> in the gap to insert. Split splits at the playhead. Saves automatically.</p>
        </div>
        <button
          type="button"
          className="section-editor-close"
          onClick={onClose}
          aria-label="Close editor"
        >
          <X size={16} strokeWidth={2.2} />
        </button>
      </header>
    );
  }

  function renderPlayer() {
    return (
      <div className="section-editor-player">
        <audio ref={audioRef} src={audioUrl!} preload="metadata" crossOrigin="anonymous" />
        <button
          type="button"
          className="mini-play-button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={14} strokeWidth={2.4} /> : <Play size={14} strokeWidth={2.4} />}
        </button>
        <span className="mini-time">{fmt(currentTime)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={(e) => seekTo(parseFloat(e.target.value))}
          className="mini-seek"
          aria-label="Seek"
        />
        <span className="mini-time mini-duration">{fmt(duration)}</span>
      </div>
    );
  }

  function renderFooter() {
    return (
      <footer className="section-editor-footer">
        <p className="footer-hint">Each correction saves immediately. Close the editor when you're done.</p>
        <div className="section-editor-footer-actions">
          <button
            type="button"
            className="section-editor-reset"
            onClick={async () => {
              if (!window.confirm('Clear every correction for this track? The classifier output will come back as-is.')) return;
              try { await onClearAll(); } catch (err) { console.warn('clearAll failed', err); }
            }}
            title="Discard every type / boundary correction and inserted section for this track"
          >
            Reset corrections
          </button>
          <button type="button" className="section-editor-done" onClick={onClose}>Done</button>
        </div>
      </footer>
    );
  }

  function renderList() {
    const rows: JSX.Element[] = [];
    sections.forEach((section, idx) => {
      // Show the insert row ONLY when a real gap exists between two
      // sections. Dragging a boundary (auto-link is off) is how the user
      // creates the gap; the insert button then fills it. Tried always-on
      // earlier — the overlapping inserts got auto-merged in the sort and
      // looked like a snap-back to the user.
      if (idx > 0) {
        const prev = sections[idx - 1];
        const gap = section.startTime - prev.endTime;
        if (gap > 0.5) {
          rows.push(
            <GapInsertRow
              key={`gap-${idx}`}
              startTime={prev.endTime}
              endTime={section.startTime}
              onAdd={async () => {
                try {
                  await onSubmitInsert('verse', prev.endTime, section.startTime);
                } catch {/* hook rolled back */}
              }}
            />,
          );
        }
      }
      rows.push(
        <SectionRow
          key={idx}
          idx={idx}
          section={section}
          sections={sections}
          totalDuration={totalDuration}
          currentTime={currentTime}
          isSaved={savedIndex === idx}
          isOpen={openTypeRow === idx}
          onToggleType={() =>
            setOpenTypeRow((cur) => (cur === idx ? null : idx))
          }
          onPlay={() => playSection(section.startTime)}
          onPickType={async (t) => {
            setOpenTypeRow(null);
            if (t === section.type) return;
            try {
              if (section.insertId) {
                await onEditInsert(section.insertId, { type: t });
              } else {
                // Use the classifier-emission index, not the merged-list
                // index. Inserts + earlier boundary edits can reorder the
                // merged list; writing the override under the wrong index
                // makes the visual snap back on next render.
                await onSubmitType(section.classifierIndex ?? idx, section.type, t);
              }
              flashSaved(idx);
            } catch {/* hook rolled back */}
          }}
          onDelete={
            section.insertId
              ? async () => {
                  try {
                    await onRemoveInsert(section.insertId!);
                  } catch {/* hook rolled back */}
                }
              : undefined
          }
          onSplitAtPlayhead={async () => {
            // Compute split point — playhead if it lands inside this
            // section with ≥1s margin on either side, otherwise the
            // section's midpoint. So Split is always usable; the user
            // doesn't have to scrub first.
            const sectionDuration = section.endTime - section.startTime;
            if (sectionDuration < 2) return; // can't split a < 2s section
            const playheadInside =
              currentTime > section.startTime + 1 &&
              currentTime < section.endTime - 1;
            const split = playheadInside
              ? currentTime
              : (section.startTime + section.endTime) / 2;
            try {
              await onSubmitBoundary(
                section.classifierIndex ?? idx,
                section.type,
                section.startTime,
                section.endTime,
                null,
                split,
              );
              await onSubmitInsert('verse', split, section.endTime);
              flashSaved(idx);
            } catch {/* hook rolled back */}
          }}
          onCommitBoundary={async (newStart, newEnd) => {
            try {
              if (section.insertId) {
                // User-inserted section — edit through the insert path.
                await onEditInsert(section.insertId, {
                  ...(newStart !== section.startTime ? { startTime: newStart } : {}),
                  ...(newEnd !== section.endTime ? { endTime: newEnd } : {}),
                });
              } else {
                await onSubmitBoundary(
                  section.classifierIndex ?? idx,
                  section.type,
                  section.startTime,
                  section.endTime,
                  newStart === section.startTime ? null : newStart,
                  newEnd === section.endTime ? null : newEnd,
                );
              }
              // Auto-link was removed — dragging one boundary used to drag
              // the neighbour's adjacent boundary too, which felt invasive.
              // Gaps that open up now get a visible insert button via
              // GapInsertRow (rendered between every pair of rows below).
              flashSaved(idx);
            } catch {
              /* hook rolled back */
            }
          }}
        />,
      );
    });
    return <ul className="section-editor-list">{rows}</ul>;
  }
}

function GapInsertRow({
  startTime,
  endTime,
  onAdd,
}: {
  startTime: number;
  endTime: number;
  onAdd: () => void | Promise<void>;
}) {
  const fmt = (t: number) =>
    `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;
  return (
    <li className="section-editor-gap">
      <div className="gap-line" />
      <button
        type="button"
        className="gap-add-button"
        onClick={onAdd}
        title="Insert a new section to fill this gap"
      >
        <Plus size={12} strokeWidth={2.4} />
        <span>Add section · {fmt(startTime)} → {fmt(endTime)}</span>
      </button>
      <div className="gap-line" />
    </li>
  );
}

// ───────────────────────── one row in the list ─────────────────────────

interface SectionRowProps {
  idx: number;
  section: Section;
  sections: Section[];
  totalDuration: number;
  currentTime: number;
  isSaved: boolean;
  isOpen: boolean;
  onToggleType: () => void;
  onPlay: () => void;
  onPickType: (t: SectionType) => void | Promise<void>;
  onCommitBoundary: (startTime: number, endTime: number) => Promise<void>;
  onSplitAtPlayhead: () => void | Promise<void>;
  /** Provided only for user-inserted sections — undefined hides the delete button. */
  onDelete?: () => void | Promise<void>;
}

function SectionRow({
  idx,
  section,
  sections,
  totalDuration,
  currentTime,
  isSaved,
  isOpen,
  onToggleType,
  onPlay,
  onPickType,
  onCommitBoundary,
  onSplitAtPlayhead,
  onDelete,
}: SectionRowProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  // Local in-progress drag state — committed on pointerup.
  const [draftStart, setDraftStart] = useState<number | null>(null);
  const [draftEnd, setDraftEnd] = useState<number | null>(null);
  const dragKindRef = useRef<'start' | 'end' | null>(null);

  // Drag freely along the track — only block crossing the section's
  // OWN opposite boundary and the track edges. Neighbours stay put on
  // drag; the new gap / overlap is rendered as-is. (Snapping to the
  // previous/next section's edge was the "auto-snap" complaint.)
  const minStart = 0;
  const maxStart = (draftEnd ?? section.endTime) - 0.1;
  const minEnd = (draftStart ?? section.startTime) + 0.1;
  const maxEnd = totalDuration;

  const startTime = draftStart ?? section.startTime;
  const endTime = draftEnd ?? section.endTime;

  const fmtT = (t: number) =>
    `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;

  const handlePointerDown = (kind: 'start' | 'end') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragKindRef.current = kind;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    if (kind === 'start') setDraftStart(section.startTime);
    else setDraftEnd(section.endTime);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const kind = dragKindRef.current;
    if (!kind || !trackRef.current || !totalDuration) return;
    const rect = trackRef.current.getBoundingClientRect();
    const px = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const t = (px / rect.width) * totalDuration;
    if (kind === 'start') {
      setDraftStart(Math.max(minStart, Math.min(maxStart, t)));
    } else {
      setDraftEnd(Math.max(minEnd, Math.min(maxEnd, t)));
    }
  };

  const handlePointerUp = async (e: React.PointerEvent) => {
    const kind = dragKindRef.current;
    if (!kind) return;
    dragKindRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch { /* ignore */ }
    const finalStart = draftStart ?? section.startTime;
    const finalEnd = draftEnd ?? section.endTime;
    setDraftStart(null);
    setDraftEnd(null);
    if (finalStart === section.startTime && finalEnd === section.endTime) return;
    await onCommitBoundary(finalStart, finalEnd);
  };

  const isCurrent = currentTime >= startTime && currentTime < endTime;
  const leftPct = totalDuration > 0 ? (startTime / totalDuration) * 100 : 0;
  const widthPct = totalDuration > 0 ? ((endTime - startTime) / totalDuration) * 100 : 0;
  const playheadPct = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <li
      className={`section-editor-row section-marker-${section.type} ${isCurrent ? 'is-playing' : ''}`}
    >
      <button
        type="button"
        className="row-play"
        onClick={onPlay}
        aria-label={`Play ${getSectionLabel(section)}`}
        title="Play from this section"
      >
        <Play size={12} strokeWidth={2.4} />
      </button>
      <div className="row-meta">
        <div className="row-label">
          {getSectionLabel(section)}
          {section.insertId && <span className="row-tag-inserted">User</span>}
        </div>
        <div className="row-time">
          {fmtT(startTime)} — {fmtT(endTime)}
        </div>
      </div>
      <div className="row-type-wrap">
        <button
          type="button"
          className={`row-type-trigger ${isOpen ? 'is-open' : ''}`}
          onClick={onToggleType}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-label={`Correct type for ${getSectionLabel(section)} — currently ${getSectionDisplayName(section.type)}`}
        >
          <span>{getSectionDisplayName(section.type)}</span>
          <ChevronDown size={11} strokeWidth={2.4} />
        </button>
        {isOpen && (
          <div className="row-type-popover" role="menu">
            {PICKER_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                role="menuitem"
                className={`row-type-option ${t === section.type ? 'is-current' : ''}`}
                onClick={() => onPickType(t)}
              >
                <span>{getSectionDisplayName(t)}</span>
                {t === section.type && <Check size={11} strokeWidth={2.6} />}
              </button>
            ))}
          </div>
        )}
      </div>
      {(() => {
        const sectionDuration = endTime - startTime;
        const tooShort = sectionDuration < 2;
        const playheadInside =
          currentTime > startTime + 1 && currentTime < endTime - 1;
        const splitAt = playheadInside
          ? currentTime
          : (startTime + endTime) / 2;
        const splitLabel = `${Math.floor(splitAt / 60)}:${Math.floor(splitAt % 60).toString().padStart(2, '0')}`;
        return (
          <button
            type="button"
            className="row-split"
            onClick={onSplitAtPlayhead}
            disabled={tooShort}
            title={
              tooShort
                ? 'Section too short to split (< 2 s)'
                : playheadInside
                ? `Split at the playhead (${splitLabel})`
                : `Split at the midpoint (${splitLabel}). Move the playhead to split somewhere else.`
            }
          >
            <Scissors size={11} strokeWidth={2.4} />
            <span>Split</span>
          </button>
        );
      })()}
      {onDelete && (
        <button
          type="button"
          className="row-delete"
          onClick={() => onDelete()}
          title="Delete this user-inserted section"
          aria-label="Delete section"
        >
          <Trash2 size={11} strokeWidth={2.4} />
        </button>
      )}
      <span className={`row-saved-indicator ${isSaved ? 'is-visible' : ''}`} aria-hidden="true">
        <Check size={12} strokeWidth={2.6} /> Saved
      </span>

      {/* Mini timeline with drag handles — full row width, below the meta. */}
      <div
        ref={trackRef}
        className="row-bounds-track"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div
          className="row-bounds-block"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        >
          <span
            className="row-bounds-handle row-bounds-handle-start"
            onPointerDown={handlePointerDown('start')}
            role="slider"
            aria-label="Section start"
            aria-valuenow={startTime}
            aria-valuemin={minStart}
            aria-valuemax={maxStart}
          />
          <span
            className="row-bounds-handle row-bounds-handle-end"
            onPointerDown={handlePointerDown('end')}
            role="slider"
            aria-label="Section end"
            aria-valuenow={endTime}
            aria-valuemin={minEnd}
            aria-valuemax={maxEnd}
          />
        </div>
        {currentTime > 0 && (
          <div className="row-bounds-playhead" style={{ left: `${playheadPct}%` }} />
        )}
      </div>
    </li>
  );
}

